import { useState } from "react";
import { ChevronDown, ChevronUp, Plus, Trash2 } from "lucide-react";
import type { SeedCluster } from "../lib/types";

interface Props {
  value: SeedCluster[] | null;
  onChange: (next: SeedCluster[] | null) => void;
}

const MAX_SEEDS = 5;

/**
 * Optional, collapsible editor for up to 5 seed clusters per question.
 * - Empty list collapses by default.
 * - Each row: label (required, max 40 chars) + hypothesis (optional, one line).
 * - Empty rows (no label) are filtered out on emit.
 * - When the cleaned list is empty, emits `null` instead of `[]` so the DB
 *   column stays NULL (semantically distinct from "user touched it but cleared
 *   everything" — both end up NULL, and that's correct).
 */
export default function SeedClustersEditor({ value, onChange }: Props) {
  const initial = value ?? [];
  const [expanded, setExpanded] = useState<boolean>(initial.length > 0);
  const [rows, setRows] = useState<SeedCluster[]>(initial);

  const emit = (next: SeedCluster[]) => {
    setRows(next);
    const cleaned = next.filter((r) => r.label.trim().length > 0);
    onChange(cleaned.length === 0 ? null : cleaned);
  };

  const updateRow = (idx: number, patch: Partial<SeedCluster>) => {
    const next = rows.map((r, i) => (i === idx ? { ...r, ...patch } : r));
    emit(next);
  };

  const addRow = () => {
    if (rows.length >= MAX_SEEDS) return;
    emit([...rows, { label: "", hypothesis: "" }]);
  };

  const removeRow = (idx: number) => {
    emit(rows.filter((_, i) => i !== idx));
  };

  return (
    <div className="border border-rc-fog rounded-xl bg-rc-paper">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
        aria-expanded={expanded}
      >
        <span className="flex flex-col">
          <span className="font-semibold text-[14px] text-rc-black">Erwartete Themen (Hypothese)</span>
          <span className="text-[12px] text-rc-slate/80 mt-0.5">
            Die KI nutzt deine Hypothesen als Ausgangspunkt. Sie kann bestätigen, umbenennen, ergänzen oder ersetzen.
          </span>
        </span>
        {expanded ? <ChevronUp className="w-4 h-4 text-rc-slate" /> : <ChevronDown className="w-4 h-4 text-rc-slate" />}
      </button>
      {expanded && (
        <div className="px-4 pb-4 flex flex-col gap-3">
          {rows.map((row, idx) => (
            <div key={idx} className="flex items-start gap-2">
              <div className="flex-1 flex flex-col gap-2">
                <input
                  type="text"
                  value={row.label}
                  maxLength={40}
                  onChange={(e) => updateRow(idx, { label: e.target.value })}
                  placeholder="Label (z. B. Eilenriede)"
                  className="w-full bg-rc-paper border-[1.5px] border-rc-fog rounded-xl px-3 py-2 text-[14px] text-rc-black focus:outline-none focus:border-rc-berry focus:ring-2 focus:ring-rc-berry/15 transition-colors"
                />
                <input
                  type="text"
                  value={row.hypothesis}
                  maxLength={120}
                  onChange={(e) => updateRow(idx, { hypothesis: e.target.value })}
                  placeholder="Hypothese (optional, ein Satz)"
                  className="w-full bg-rc-paper border-[1.5px] border-rc-fog rounded-xl px-3 py-2 text-[13px] text-rc-slate focus:outline-none focus:border-rc-berry focus:ring-2 focus:ring-rc-berry/15 transition-colors"
                />
              </div>
              <button
                type="button"
                onClick={() => removeRow(idx)}
                aria-label="Hypothese entfernen"
                className="mt-1 p-2 text-rc-slate hover:text-rc-danger transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          {rows.length < MAX_SEEDS && (
            <button
              type="button"
              onClick={addRow}
              className="inline-flex items-center gap-2 self-start text-[13px] font-semibold text-rc-berry hover:text-rc-berry-700 transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              Hypothese hinzufügen
            </button>
          )}
          {rows.length === 0 && (
            <p className="text-[12px] text-rc-slate/80 italic">
              Leer lassen, wenn du keine Hypothese hast. Die KI entdeckt die Themen dann ohne Vorgaben.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
